import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { IQuestion, READ_QUESTIONS_MAX } from 'cat-shared/utils'
import { Direction } from 'client/utils'
import { usePage, usePageStateContext } from 'client/hooks'
import { AppGroup, AppParagraph } from 'client/ui-building-blocks'
import { type TGetQuestionsData } from 'client/api/useGetQuestions/useGetQuestions'
import { QuestionContextMenu } from 'client/components/Question/QuestionContextMenu'

type Props = {
  questionsData: TGetQuestionsData
  question?: IQuestion
  isDisabled?: boolean
}

export const QuestionModalHeader = ({
  questionsData = { count: 0, data: [] },
  question = null,
  isDisabled = false,
}: Props) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()

  const [currentIndex, setCurrentIndex] = useState<number>(-1)

  const { navigateTo } = usePage()
  const { browseQuestionsPageState } = usePageStateContext()
  const { pageNo } = browseQuestionsPageState

  const questionId = question?._id?.toString() || null

  useEffect(() => {
    if (question && questionsData) {
      if (pathname === '/home') {
        setCurrentIndex(questionsData.data.findIndex(q => q._id === questionId))
      } else if (pathname === '/browse') {
        setCurrentIndex(
          questionsData.data.findIndex(q => q._id === questionId) +
            (pageNo - 1) * READ_QUESTIONS_MAX
        )
      }
    }
  }, [pathname, pageNo, questionId, question, questionsData, questionsData?.data])

  const onNavigate = (direction: Direction) => {
    if (isDisabled) {
      return
    }

    document.getElementById('submit-form-reset-btn')?.click()

    const currentIndex = questionsData.data.findIndex(q => q._id === questionId)
    let newIndex: number

    if (direction === Direction.Prev) {
      if (currentIndex > 0) {
        newIndex = currentIndex - 1
      } else {
        newIndex = questionsData.data.length - 1
      }
    } else {
      if (currentIndex < questionsData.data.length - 1) {
        newIndex = currentIndex + 1
      } else {
        newIndex = 0
      }
    }

    navigateTo({
      path: pathname,
      query: { questionId: questionsData.data[newIndex]._id.toString() },
      preventScroll: true,
    })
  }

  const onNavPrevClick = () => {
    onNavigate(Direction.Prev)
  }

  const onNavNextClick = () => {
    onNavigate(Direction.Next)
  }

  const current = currentIndex + 1

  if (!questionsData?.data?.find(q => q._id.toString() === questionId)) {
    return null
  }

  const hideNav = currentIndex === -1 || !questionsData

  const first = (pageNo - 1) * READ_QUESTIONS_MAX + 1
  const last = (pageNo - 1) * READ_QUESTIONS_MAX + questionsData.data.length

  return (
    <AppGroup position="spaceBetween">
      <div>
        {!hideNav ? (
          <>
            <button
              id="question-nav-prev"
              onClick={onNavPrevClick}
              style={{ display: 'none' }}
            />
            <button
              id="question-nav-next"
              onClick={onNavNextClick}
              style={{ display: 'none' }}
            />
            <AppParagraph
              isHeader
              size="small"
              text={
                questionsData.data.length > 1
                  ? t('question.nav.multiple_pages', { pageNo, current, first, last })
                  : t('question.nav.one_page', { pageNo, current })
              }
            />
          </>
        ) : null}
      </div>
      <QuestionContextMenu
        question={question}
        isDisabled={isDisabled}
        pointing="top right"
      />
    </AppGroup>
  )
}
